import { Card, Group, Stack, Table, Text } from '@mantine/core';
import { DotPlot } from '@/components/dot-plot';
import { PageHeader } from '@/components/page-header';
import { SampleDataBanner } from '@/components/sample-data-banner';
import { SectionHeading } from '@/components/section-heading';
import { StatTile } from '@/components/stat-tile';
import { signals } from '@/lib/fixtures/analytics';
import classes from './analytics-page.module.css';

const format = new Intl.NumberFormat('en-GB');

export function SignalsPage() {
  const { installs, coverage, levelMoves } = signals;
  const retention = (signals.secondSessions / signals.firstRuns) * 100;
  const pastAssessment = coverage.reduce(
    (max, row) => Math.max(max, row.touched),
    0,
  );

  return (
    <Stack gap="xl" className={classes.page}>
      <SampleDataBanner detail="The metrics docs/analytics.md deliberately keeps out of the funnel, because none of them nest inside the rung above." />

      <PageHeader
        title="Signals"
        description="Measured alongside the funnel, never as rungs in it."
        actions={
          <Text size="sm" c="dimmed">
            {signals.window}
          </Text>
        }
      />

      <Card>
        <div className={classes.hero}>
          <StatTile
            variant="plain"
            size="lg"
            label="Second session"
            value={`${retention.toFixed(0)}%`}
            tone="teal"
            secondary="Came back on a later day"
            note="Not orderable against registration: somebody who signs in thirty seconds after Act III has registered without having had a second session, and could not possibly have had one yet. That is why it sits here and not in the funnel."
          />
          <div className={classes.heroDivider} />
          <StatTile
            variant="plain"
            label="First runs"
            value={format.format(signals.firstRuns)}
            secondary="The denominator above"
          />
          <StatTile
            variant="plain"
            label="Came back"
            value={format.format(signals.secondSessions)}
            secondary="On a later calendar day"
            note="A calendar day rather than the usual thirty minutes of inactivity. Somebody who does the three-minute first run and then plays for another forty minutes had one very good session, not two."
          />
          <StatTile
            variant="plain"
            label="Installs"
            value={format.format(installs.total)}
            tone="dimmed"
            secondary={installs.source}
            note={installs.caveat}
          />
        </div>
      </Card>

      <Stack gap="sm">
        <SectionHeading
          eyebrow="Surfaces"
          title="Coverage"
          subtitle="A matrix, not a ranking. Neither surface is further along than the other."
          note="Opening a surface and using it are different events, and the gap between the two marks is the point: it is the difference between measuring navigation and measuring the thing the product claims to train."
        />
        <Card>
          <DotPlot
            max={pastAssessment}
            fromLabel="Opened it"
            toLabel="Cleared the bar"
            rows={coverage.map((row) => ({
              label: row.surface,
              detail: row.bar,
              from: row.touched,
              to: row.counted,
            }))}
          />
        </Card>
      </Stack>

      <Stack gap="sm">
        <SectionHeading
          eyebrow="Progression"
          title="Level movement"
          subtitle="A user's level is who they can hold a conversation with, so progress is a move between people."
          note="Medians rather than means: a handful of users who took six months would drag an average somewhere nobody actually is. This is the product's central claim stated as a number."
        />
        <Card p={0}>
          <Table.ScrollContainer minWidth={440}>
            <Table verticalSpacing="sm" horizontalSpacing="lg">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Move</Table.Th>
                  <Table.Th className={classes.numeric}>Users</Table.Th>
                  <Table.Th className={classes.numeric}>Median time</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {levelMoves.map((move) => (
                  <Table.Tr key={`${move.from}-${move.to}`}>
                    <Table.Td>
                      <Group gap="xs" wrap="nowrap">
                        <Text size="sm" c="dimmed">
                          {move.from}
                        </Text>
                        <Text size="sm" c="dimmed">
                          →
                        </Text>
                        <Text size="sm" fw={600}>
                          {move.to}
                        </Text>
                      </Group>
                    </Table.Td>
                    <Table.Td className={classes.numeric}>
                      <Text size="sm">{format.format(move.users)}</Text>
                    </Table.Td>
                    <Table.Td className={classes.numeric}>
                      <Text size="sm">{move.medianDays} days</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        </Card>
      </Stack>
    </Stack>
  );
}
